import { createSlice } from "@reduxjs/toolkit";

import {
  buildLinkedItemTargets,
  forEachTargetState,
  stripImageRelations,
} from "./linkedItemHelpers";

const prequalificationSlice = createSlice({
  name: "prequalification",
  initialState: [],
  reducers: {
    prequalificationAdd(state, action) {
      const newItems = action.payload;
      return newItems || [];
    },

    prequalificationItemAdd(state, action) {
      const { prequalificationId, prequalificationItemList } = action.payload;

      // Find the prequalification by ID
      const prequalification = state.find(item => item.id === prequalificationId);

      if (prequalification && prequalificationItemList) {
        // If the prequalification exists, overwrite its items with the new list
        prequalification.prequalificationItem = prequalificationItemList;
      }
    },

    prequalificationDelete(state, action) {
      const { file } = action.payload;
      const { prequalificationId, id: fileId } = file;

      const targets = buildLinkedItemTargets(file.prequalificationItem, "prequalification");
      if (targets.size > 0) {
        forEachTargetState(
          state,
          targets,
          (_prequalificationState, prequalificationItemById, itemIds) => {
            for (const itemId of itemIds) {
              const prequalificationItem = prequalificationItemById.get(itemId);
              if (!prequalificationItem?.image) {
                continue;
              }

              prequalificationItem.image = prequalificationItem.image.filter(
                img => img.id !== fileId,
              );
            }
          },
        );
        return;
      }

      // Only the items holding the file change, so the others keep their references
      const prequalification = state.find(item => item.id === prequalificationId);
      for (const spec of prequalification?.prequalificationItem || []) {
        if (spec.image?.some(img => img.id === fileId)) {
          spec.image = spec.image.filter(img => img.id !== fileId);
        }
      }
    },

    prequalificationSpecificAdd(state, action) {
      const { id, response: newImage, prequalificationId } = action.payload;
      const prequalification = state.find(item => item.id === prequalificationId);

      if (prequalification) {
        const preqItem = prequalification.prequalificationItem?.find(item => item.id === id);
        if (preqItem) {
          if (preqItem.image) {
            preqItem.image.push(newImage);
          } else {
            preqItem.image = [newImage];
          }
        }
      }
    },

    prequalificationItemDelete(state, action) {
      const { itemId } = action.payload;

      // Find and update prequalification containing this item
      for (const preq of state) {
        if (preq.prequalificationItem) {
          preq.prequalificationItem = preq.prequalificationItem.filter(item => item.id !== itemId);
        }
      }
    },

    prequalificationReplace(state, action) {
      const { prequalificationId, prequalification } = action.payload;
      const prequalificationIndex = state.findIndex(item => item.id === prequalificationId);

      if (prequalificationIndex !== -1) {
        state[prequalificationIndex] = prequalification;
      }
      // Logic for handling prequalification request can be added here.
    },

    prequalificationItemUpdate(state, action) {
      const updatedPreqItem = action.payload;

      for (const prequalification of updatedPreqItem.prequalification || []) {
        const currentPrequalification = state.find(item => item.id === prequalification.id);
        if (!currentPrequalification) {
          continue;
        }

        // A prequalification loaded without its items has nothing to replace; throwing here
        // would discard the update for every other prequalification as well
        const preqItemIndex =
          currentPrequalification.prequalificationItem?.findIndex(
            item => item.id === updatedPreqItem.id,
          ) ?? -1;
        if (preqItemIndex !== -1) {
          currentPrequalification.prequalificationItem[preqItemIndex] = updatedPreqItem;
        }
      }
    },

    prequalificationItemCommentUpdate(state, action) {
      const comment = action.payload;

      const commentTargetsByPrequalificationId = buildLinkedItemTargets(
        comment.prequalificationItem,
        "prequalification",
      );

      forEachTargetState(
        state,
        commentTargetsByPrequalificationId,
        (_prequalificationState, prequalificationItemById, itemIds) => {
          for (const itemId of itemIds) {
            const prequalificationItem = prequalificationItemById.get(itemId);
            if (!prequalificationItem) {
              continue;
            }

            const commentIndex =
              prequalificationItem.comment?.findIndex(item => item.id === comment.id) ?? -1;
            if (commentIndex !== -1) {
              prequalificationItem.comment[commentIndex] = comment;
            }
          }
        },
      );
    },

    prequalificationItemImageUpdate(state, action) {
      const imageObject = action.payload;

      const imageTargetsByPrequalificationId = buildLinkedItemTargets(
        imageObject.prequalificationItem,
        "prequalification",
      );
      const normalizedImage = stripImageRelations(imageObject);

      forEachTargetState(
        state,
        imageTargetsByPrequalificationId,
        (_prequalificationState, prequalificationItemById, itemIds) => {
          for (const itemId of itemIds) {
            const prequalificationItem = prequalificationItemById.get(itemId);
            if (!prequalificationItem) {
              continue;
            }

            const imageIndex =
              prequalificationItem.image?.findIndex(img => img.id === imageObject.id) ?? -1;
            if (imageIndex !== -1) {
              prequalificationItem.image[imageIndex] = { ...normalizedImage };
            }
          }
        },
      );
    },

    prequalificationMoveImage(state, action) {
      const { payload: image, destinationId, preqItemId } = action.payload;

      const imageTargetsByPrequalificationId = buildLinkedItemTargets(
        image.prequalificationItem,
        "prequalification",
      );
      const normalizedImage = stripImageRelations(image);

      forEachTargetState(
        state,
        imageTargetsByPrequalificationId,
        (_prequalificationState, prequalificationItemById, sourceItemIds) => {
          if (!sourceItemIds.has(preqItemId)) {
            return;
          }

          const fromItem = prequalificationItemById.get(preqItemId);
          const toItem = prequalificationItemById.get(destinationId);
          if (!fromItem || !toItem) {
            return;
          }

          const imageIndex = fromItem.image?.findIndex(img => img.id === image.id) ?? -1;
          if (imageIndex === -1) {
            return;
          }

          fromItem.image.splice(imageIndex, 1);

          const destinationImageIndex = toItem.image?.findIndex(img => img.id === image.id) ?? -1;
          if (destinationImageIndex === -1) {
            if (Array.isArray(toItem.image)) {
              toItem.image.push({ ...normalizedImage });
            } else {
              toItem.image = [{ ...normalizedImage }];
            }
          } else {
            toItem.image[destinationImageIndex] = { ...normalizedImage };
          }
        },
      );
    },

    prequalificationItemImageNameUpdate(state, action) {
      const imageObject = action.payload;

      const imageTargetsByPrequalificationId = buildLinkedItemTargets(
        imageObject.prequalificationItem,
        "prequalification",
      );

      forEachTargetState(
        state,
        imageTargetsByPrequalificationId,
        (_prequalificationState, prequalificationItemById, itemIds) => {
          for (const itemId of itemIds) {
            const prequalificationItem = prequalificationItemById.get(itemId);
            if (!prequalificationItem) {
              continue;
            }

            const image = prequalificationItem.image?.find(img => img.id === imageObject.id);
            if (image) {
              image.imageBaseName = imageObject.imageBaseName;
            }
          }
        },
      );
    },

    // Documents (de)activated on a requirement. Found by the requirement, in whichever
    // prequalification holds it: a document's own `prequalificationId` names the process it was
    // uploaded under, which for a carried-over document isn't the one shown.
    prequalificationItemImageStatusUpdate(state, action) {
      const { prequalificationItemId, active, id: fileId } = action.payload.file;

      for (const prequalification of state) {
        const preqItem = prequalification.prequalificationItem?.find(
          item => item.id === prequalificationItemId,
        );
        const image = preqItem?.image?.find(img => img.id === fileId);
        if (image) {
          image.active = active;
        }
      }
    },
  },
});

export const {
  prequalificationAdd,
  prequalificationItemAdd,
  prequalificationSpecificAdd,
  prequalificationDelete,
  prequalificationReplace,
  prequalificationItemUpdate,
  prequalificationItemCommentUpdate,
  prequalificationItemImageStatusUpdate,
  prequalificationItemImageUpdate,
  prequalificationMoveImage,
  prequalificationItemImageNameUpdate,
  prequalificationItemDelete,
} = prequalificationSlice.actions;

export default prequalificationSlice.reducer;
